import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
    Drawer,
    Form,
    Input,
    DatePicker,
    TreeSelect,
    Radio,
    Switch,
    Button,
    Space,
    Divider,
    Row,
    Col,
    message,
} from "antd";
import { staffAPIWithErrorHandling, staffHelpers } from "../../../api/staff/staff";
import { settings } from "../../../utils/config";

const { TextArea } = Input;

const ACCOUNT_TYPES = [
    { label: "Nhân viên", value: "STAFF" },
    { label: "Quản lý", value: "BRANCH_MANAGER" },
    { label: "Quản lý cấp phường", value: "WARD_LEADER" },
];

/**
 * Drawer thêm mới / chỉnh sửa nhân sự.
 *
 * Dữ liệu điền sẵn lấy thẳng từ dòng trên bảng: API danh sách đã nạp kèm
 * staffOrgMap.organization và user.info nên không cần gọi lại getStaffById.
 *
 * @param {boolean} open
 * @param {object|null} record   Có -> chế độ sửa, null -> chế độ thêm mới.
 * @param {object[]} treeData    Cây đơn vị (nơi gọi đã nạp sẵn).
 * @param {function} onClose
 * @param {function} onSaved     Gọi sau khi lưu thành công để nơi gọi nạp lại dữ liệu.
 */
export default function StaffFormDrawer({
    open,
    record = null,
    treeData = [],
    onClose,
    onSaved,
}) {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const isEdit = !!record;

    const typeUser = settings?.getCookie("type_user");
    const accountType = Form.useWatch("account_type", form);
    const isWardLeaderAccount = accountType === "WARD_LEADER";
    const isBranchManagerAccount = accountType === "BRANCH_MANAGER";

    // Chỉ cấp được vai trò thấp hơn hoặc bằng mình
    const allowedAccountTypes = useMemo(() => {
        if (typeUser === "SHOP") return ACCOUNT_TYPES;
        if (typeUser === "WARD_LEADER") {
            return ACCOUNT_TYPES.filter(
                (item) => item.value === "STAFF" || item.value === "BRANCH_MANAGER"
            );
        }
        return ACCOUNT_TYPES.filter((item) => item.value === "STAFF");
    }, [typeUser]);

    const defaultAccountType = allowedAccountTypes[0]?.value || "STAFF";

    useEffect(() => {
        if (!open) return;

        if (isEdit) {
            const currentType = record.user?.info?.type;
            // API mới trả mảng `organizations`; `staff_org_map` là dạng cũ một
            // đơn vị, giữ lại để bản ghi chưa được nạp lại vẫn hiện đúng.
            const orgIds = record.organizations?.length
                ? record.organizations.map((item) => item.id)
                : [record.staff_org_map?.organization_id].filter(Boolean);

            form.setFieldsValue({
                name: record.name,
                email: record.email,
                phone: record.phone,
                dob: record.dob ? dayjs(record.dob) : null,
                address: record.address,
                // treeData dựng value bằng String(id) còn API trả số: không ép kiểu
                // thì ô đơn vị hiện ID trần thay vì tên (lỗi có sẵn ở trang sửa cũ).
                organization_ids: orgIds.map(String),
                // Vai trò của người đang sửa có thể cao hơn quyền của mình -> lùi về
                // vai trò cao nhất mình được cấp, tránh gửi lên giá trị bị backend chặn.
                account_type: allowedAccountTypes.some((item) => item.value === currentType)
                    ? currentType
                    : defaultAccountType,
                password: undefined,
                status: !!record.status,
            });
        } else {
            form.resetFields();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, record]);

    // Quản lý cấp phường không gắn vào đơn vị nào
    useEffect(() => {
        if (open && isWardLeaderAccount) {
            form.setFieldValue("organization_ids", undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isWardLeaderAccount]);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const staffData = staffHelpers.formatStaffData({
                ...values,
                organization_ids:
                    values.account_type === "WARD_LEADER"
                        ? []
                        : (values.organization_ids || []).map(Number),
                dob: values.dob ? values.dob.format?.("YYYY-MM-DD") || values.dob : null,
            });

            const validation = staffHelpers.validateStaffData(staffData);
            if (!validation.isValid) {
                Object.keys(validation.errors).forEach((field) => {
                    form.setFields([{ name: field, errors: [validation.errors[field]] }]);
                });
                return;
            }

            if (isEdit) {
                await staffAPIWithErrorHandling.updateStaff(record.id, staffData);
                message.success("Cập nhật thông tin nhân sự thành công!");
            } else {
                await staffAPIWithErrorHandling.createStaff(staffData);
                message.success("Thêm nhân sự thành công!");
            }

            onSaved?.();
            onClose?.();
        } catch (error) {
            // Drawer hẹp, toast dễ bị bỏ qua nên lỗi theo trường phải hiện ngay tại ô.
            if (error.fieldErrors) {
                form.setFields(
                    Object.entries(error.fieldErrors).map(([name, errors]) => ({
                        name,
                        errors: Array.isArray(errors) ? errors : [errors],
                    }))
                );
                message.error("Dữ liệu chưa hợp lệ, kiểm tra lại các ô được đánh dấu.");
            } else {
                message.error(
                    error.message || (isEdit ? "Không thể cập nhật nhân sự" : "Không thể thêm nhân sự")
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            title={isEdit ? "Chỉnh sửa nhân sự" : "Thêm nhân sự"}
            width="min(640px, 100vw)"
            open={open}
            onClose={onClose}
            destroyOnHidden
            maskClosable={!submitting}
            footer={
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={onClose} disabled={submitting}>
                        Hủy
                    </Button>
                    <Button type="primary" loading={submitting} onClick={() => form.submit()}>
                        {isEdit ? "Lưu thay đổi" : "Thêm mới"}
                    </Button>
                </Space>
            }
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark
                initialValues={{ status: true, account_type: defaultAccountType }}
            >
                <Form.Item
                    name="name"
                    label="Họ và tên"
                    rules={[
                        { required: true, message: "Vui lòng nhập họ và tên!" },
                        { min: 2, message: "Họ tên phải có ít nhất 2 ký tự!" },
                        { max: 100, message: "Họ tên không được vượt quá 100 ký tự!" },
                    ]}
                >
                    <Input placeholder="Nhập họ và tên đầy đủ" />
                </Form.Item>

                {/* Ghép hai ô ngắn nhất vào một hàng, để Email giữ trọn bề ngang */}
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="phone"
                            label="Số điện thoại (ưu tiên Zalo)"
                            rules={[
                                { required: true, message: "Vui lòng nhập số điện thoại!" },
                                { pattern: /^[0-9]{10,11}$/, message: "Số điện thoại phải có 10-11 chữ số!" },
                            ]}
                        >
                            <Input placeholder="0912345678" maxLength={11} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="dob"
                            label="Ngày sinh"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (value && new Date(value) > new Date()) {
                                            return Promise.reject("Ngày sinh không thể lớn hơn ngày hiện tại!");
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <DatePicker
                                placeholder="Chọn ngày sinh"
                                format="DD/MM/YYYY"
                                style={{ width: "100%" }}
                                disabledDate={(current) => current && new Date(current) > new Date()}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="email"
                    label="Email"
                    rules={[{ type: "email", message: "Email không hợp lệ!" }]}
                >
                    <Input placeholder="example@email.com" />
                </Form.Item>

                <Form.Item name="address" label="Địa chỉ">
                    <TextArea
                        placeholder="Số nhà, đường, phường/xã, quận/huyện, tỉnh/thành phố"
                        rows={3}
                        maxLength={500}
                        showCount
                    />
                </Form.Item>

                <Divider orientation="left" plain style={{ marginTop: 8 }}>
                    Tài khoản
                </Divider>

                <Form.Item
                    name="account_type"
                    label="Vai trò tài khoản"
                    rules={[{ required: true, message: "Vui lòng chọn vai trò tài khoản" }]}
                >
                    <Radio.Group options={allowedAccountTypes} />
                </Form.Item>

                {!isWardLeaderAccount && (
                    <Form.Item
                        name="organization_ids"
                        label="Thuộc đơn vị"
                        extra={
                            isBranchManagerAccount
                                ? "Sẽ làm quản lý của tất cả đơn vị được chọn, mà mỗi đơn vị chỉ có một quản lý. Chọn đúng đơn vị cần phụ trách, không chọn đơn vị cấp trên."
                                : "Chọn được nhiều đơn vị. Chọn đúng đơn vị cần phụ trách, không chọn đơn vị cấp trên."
                        }
                        rules={[{ required: true, message: "Vui lòng chọn đơn vị trực thuộc!" }]}
                    >
                        <TreeSelect
                            multiple
                            treeDataSimpleMode
                            style={{ width: "100%" }}
                            styles={{ popup: { root: { maxHeight: 400, overflow: "auto" } } }}
                            placeholder="Chọn đơn vị"
                            treeData={treeData}
                            allowClear
                            showSearch
                            maxTagCount="responsive"
                            treeNodeFilterProp="title"
                        />
                    </Form.Item>
                )}

                <Row gutter={16}>
                    <Col xs={24} sm={14}>
                        <Form.Item
                            name="password"
                            label="Mật khẩu đăng nhập"
                            extra={
                                isEdit
                                    ? "Để trống nếu không muốn thay đổi mật khẩu."
                                    : "Để trống thì hệ thống dùng mật khẩu mặc định."
                            }
                        >
                            <Input.Password
                                placeholder={isEdit ? "Nhập mật khẩu mới (nếu muốn đổi)" : "Nhập mật khẩu (tùy chọn)"}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={10}>
                        <Form.Item name="status" label="Trạng thái tài khoản" valuePropName="checked">
                            <Switch checkedChildren="Hoạt động" unCheckedChildren="Không hoạt động" />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    );
}
